import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import entitiesHelper from "../../../../API/EntitiesAI/entitiesHelper";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import ToothFairyModal from "../../../../Components/ToothFairyComponents/ToothFairyModal";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import SettingsAI from "../SettingsAI";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import useSections from "../../../../Hooks/useSections";

const TopicsList = () => {
  const formRef = useRef();
  const { currentTheme } = AppTheme.useTheme();
  const { height } = useWindowDimensions();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [showTopicModal, setShowTopicModal] = React.useState(false);
  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedTopicId, setSelectedTopicId] = React.useState(null);
  const [isLoading] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [displayedTopics, setDisplayedTopics] = React.useState([]);
  const [selectedTopicIds, setSelectedTopicIds] = React.useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = React.useState(false);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const [initialMapValues, setInitialMapValues] = useState({
    label: "",
    description: "",
    emoji: "",
  });

  const columns = [
    {
      title: t("settings.list.id"),
      width: 0.00001,
    },
    {
      title: t("settings.list.emoji"),
      width: 0.01,
    },
    {
      title: t("settings.list.label"),
      width: 0.3,
    },
    {
      title: t("settings.list.description"),
    },

    {
      title: t("settings.list.actions"),
      isAction: true,
      width: 0.1,
    },
  ];

  const topics = useMemo(() => {
    return state?.aiEntitiesConfig?.filter((i) => i.type === "topic") || [];
  }, [state?.aiEntitiesConfig]);

  useEffect(() => {
    const sortedTopics = [...topics].sort((a, b) =>
      a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
    );
    setDisplayedTopics(sortedTopics);
  }, [topics]);

  const onTopicCreated = useCallback(
    (event) => {
      if (event === "success") {
        AppToasty.show(
          t(
            isEditMode
              ? "settings.list.topicUpdatedSuccess"
              : "settings.list.topicCreatedSuccess"
          ),
          {
            type: "success",
          }
        );
      } else {
        AppToasty.show(
          t(
            isEditMode
              ? "settings.list.topicUpdateFailed"
              : "settings.list.topicCreationFailed"
          ),
          {
            type: "danger",
          }
        );
      }
      formRef?.current?.resetForm();
      setShowTopicModal(false);
      setSelectedIndex(null);
      setSelectedTopicId(null);
      setIsEditMode(false);
    },
    [AppToasty, t, isEditMode]
  );

  const topicItemCreationComponent = useMemo(() => {
    return (
      <ToothFairyModal
        modalStyle={{
          maxHeight: isLargeScreen() ? "95%" : 500,
          height: "auto",
          maxWidth: isLargeScreen() ? 700 : 350,
          marginHorizontal: "auto",
          marginTop: "auto",
          marginBottom: "auto",
          margin: "auto",
        }}
        isModalOpen={showTopicModal}
        setIsModalOpen={setShowTopicModal}
        modalTitle={
          isEditMode
            ? t("settings.list.editTopic")
            : t("settings.list.createTopic")
        }
        closeModal={() => {
          formRef?.current?.resetForm();
          setShowTopicModal(false);
          setSelectedIndex(null);
          setSelectedTopicId(null);
        }}
      >
        <SettingsAI
          ref={formRef}
          initialValues={initialMapValues}
          onEntityCreated={onTopicCreated}
          isEdit={isEditMode}
          settingType="topic"
          sectionTitle={
            isEditMode ? initialMapValues.label : t("settings.list.newTopic")
          }
          opt={
            isEditMode && selectedTopicId
              ? identifyOptByID(selectedTopicId)
              : null
          }
        />
      </ToothFairyModal>
    );
  }, [
    showTopicModal,
    setShowTopicModal,
    JSON.stringify(initialMapValues),
    isEditMode,
    selectedIndex,
    selectedTopicId,
    displayedTopics,
  ]);

  function identifyOptByID(id) {
    return displayedTopics?.find((u) => u.id === id);
  }

  const handleTopicItemSelection = ({ id, index }) => {
    setSelectedIndex(index);
    setSelectedTopicId(id);
    setIsEditMode(true);
    let extendedItem = identifyOptByID(id);
    setInitialMapValues({
      ...extendedItem,
    });
    setShowTopicModal(true);
  };

  const handleTopicItemDeletion = async () => {
    try {
      setShowChoice(false);
      const _id = selectedTopicId || selectedItem?.id;
      if (_id) {
        const result = await entitiesHelper.deleteEntity(_id);

        const updatedEntities = state?.aiEntitiesConfig?.filter(
          (u) => u.id !== _id
        );
        dispatch("SET_AI_ENTITIES_CONFIG", updatedEntities);

        AppToasty.show(t("settings.list.topicDeletedSuccess"), {
          type: "success",
        });
        setSelectedIndex(null);
        setSelectedTopicId(null);
        setSelectedItem(null);
      } else {
        throw new Error(t("settings.list.noItemSelected"));
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("settings.list.topicDeletionFailed"), {
        type: "danger",
      });
    }
  };

  const handleBulkDeletion = async () => {
    try {
      for (const id of selectedTopicIds) {
        await entitiesHelper.deleteEntity(id);
      }
      const updatedEntities = state?.aiEntitiesConfig?.filter(
        (u) => !selectedTopicIds.includes(u.id)
      );
      dispatch("SET_AI_ENTITIES_CONFIG", updatedEntities);
      setSelectedTopicIds([]);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("settings.list.topicDeletedSuccess"), {
        type: "success",
      });
    } catch (error) {
      console.log(error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("settings.list.topicDeletionFailed"), {
        type: "danger",
      });
    }
  };

  const handleCreation = () => {
    setInitialMapValues({
      label: "",
      description: "",
      emoji: "",
    });
    setIsEditMode(false);
    setShowTopicModal(true);
  };

  const isItemActionable = () => {
    return true;
  };

  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("common.cancel")}
        caption={t("settings.list.deleteTopicConfirmation")}
        subCaption={t("settings.list.deleteTopicConfirmationMessage")}
        confirmCaption={t("common.delete")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleTopicItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem]);

  const _bulkDeleteChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteSelectedConfirmation", {
          defaultValue: t("settings.list.deleteTopicConfirmation"),
        })}
        subCaption={t("settings.list.deleteTopicConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    );
  }, [showBulkDeleteChoice, selectedTopicIds]);

  return (
    <View
      style={{
        minWidth: "100%",
        paddingLeft: 10,
        flex: 1,
        overflow: isLoading && "hidden",
      }}
    >
      {isLoading ? (
        <ToothFairyLoadingPlaceholder
          backgroundColor={currentTheme?.theme?.softBackgroundColor}
          visible={true}
          containerStyle={{
            backgroundColor: currentTheme?.theme?.transparent,
            minHeight: 250,
          }}
          caption={t("settings.list.loadingTopics")}
        />
      ) : (
        <>
          {displayedTopics.length > 0 ? (
            <AppTable
              isSelectable
              showPagination
              itemsPerPage={50}
              hideSearch={false}
              searchPlaceholder={t("settings.toolbar.searchTopics")}
              onSelectionChange={(selectedItems) => {
                setSelectedTopicIds(selectedItems?.map((u) => u?.id) || []);
              }}
              bulkActions={[
                {
                  id: "DELETE",
                  label: t("settings.list.delete"),
                  icon: AppAssets.icons.delete,
                  isDestructive: true,
                },
              ]}
              onBulkActionPress={(id) => {
                if (id === "DELETE" && selectedTopicIds?.length > 0)
                  setShowBulkDeleteChoice(true);
              }}
              actionButtons
              mapIconWithStatus={(item) => {
                return AppAssets.icons.activeModel;
              }}
              mainActionLabel={t("settings.list.view")}
              showDropDownMenuForActions
              onClickMainAction={(item, index) => {
                handleTopicItemSelection({
                  id: item?.id,
                  index: index,
                });
              }}
              actionButtonStyle={{
                minWidth: 20,
              }}
              onActionItemPress={async (id, item, index) => {
                if (id === "EDIT") {
                  handleTopicItemSelection({
                    id: item?.id,
                    index: index,
                  });
                } else if (id === "DELETE") {
                  setSelectedItem(item);
                  setSelectedIndex(index);
                  setSelectedTopicId(item?.id);
                  setShowChoice(true);
                }
              }}
              actions={[
                {
                  label: t("settings.list.edit"),
                  id: "EDIT",
                  icon: AppAssets.icons.edit,
                },
                {
                  label: t("settings.list.delete"),
                  id: "DELETE",
                  icon: AppAssets.icons.delete,
                },
              ]}
              isItemActionable={() => isItemActionable()}
              style={{
                zIndex: -1,
                marginTop: 15,
                height:
                  displayedTopics?.length >= 50 ? height - 95 : height - 75,
                paddingRight: 0,
                overflowY: "auto",
                overflowX: "hidden",
              }}
              headers={columns}
              items={displayedTopics
                ?.sort((a, b) => (a?.label || "").localeCompare(b?.label || ""))
                ?.map((u) => {
                  return {
                    id: u?.id || " ",
                    emoji: u?.emoji || " ",
                    label: u?.label || " ",
                    description:
                      u?.description ||
                      t("settings.list.noDescriptionProvided"),

                    actions: " ",
                  };
                })}
            >
              <ToothFairyButton
                title={t("settings.toolbar.create")}
                onPress={handleCreation}
              />
            </AppTable>
          ) : (
            <AppPlaceholderView
              wrapperStyle={{
                width: "100%",
                height: "100%",
                marginVertical: 20,
                marginHorizontal: "auto",
                flex: 1,
              }}
              size={30}
              icon={AppAssets.icons.collection}
              mainCaption={t("settings.list.noTopicsFound")}
              subCaption={t("settings.list.noTopicsFoundSub")}
              actionLabel={t("settings.toolbar.create")}
              onActionPress={handleCreation}
            />
          )}
        </>
      )}
      {topicItemCreationComponent}
      {_coreChoice}
      {_bulkDeleteChoice}
    </View>
  );
};

export default TopicsList;
